import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  CreditCard,
  Package,
  AlertTriangle,
  BarChart3,
  Users,
  Calendar,
  ArrowRight,
  Store,
  Globe,
  Receipt,
  Clock,
  Award,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Sun,
  Moon,
  ChevronDown,
  Check,
  Truck,
  Database,
} from 'lucide-react';
import { MigrarLotesModal } from '../components/maintenance/MigrarLotesModal';
import { Listbox, Transition } from '@headlessui/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ComposedChart,
  LabelList,
} from 'recharts';
import { ReportsService } from '../services/reports/reports.service';
import { ProductsService } from '../services/products/products.service';
import { UsersService } from '../services/users/users.service';
import { SuppliersService } from '../services/suppliers/suppliers.service';
import { getProductImageUrl } from '../utils/product-images';
import { getColombiaDate, toColombiaDateString } from '../utils/date';
import { useOrderWebSocket } from '../hooks/useOrderWebSocket';
import { Tabs } from '../components/ui/Tabs';
import toast from 'react-hot-toast';

/* ─── Types ─── */
interface DashboardData {
  hoy: { totales: { ventasOnline: number; ventasPOS: number; totalGeneral: number; cantidadOnline: number; cantidadPOS: number; totalTransacciones: number; }; promedios: { ticketPromedioOnline: number; ticketPromedioPOS: number; ticketPromedioGeneral: number; }; };
  mes: { totales: { ventasOnline: number; ventasPOS: number; totalGeneral: number; cantidadOnline: number; cantidadPOS: number; totalTransacciones: number; }; promedios: { ticketPromedioOnline: number; ticketPromedioPOS: number; ticketPromedioGeneral: number; }; };
  topProductos: Array<{ id: string; nombre: string; categoria: string; cantidadVendida: number; totalVentas: number; }>;
  bottomProductos: Array<{ id: string; nombre: string; categoria: string; cantidadVendida: number; totalVentas: number; stockBase: number; }>;
  lowStockProducts: Array<{ id: string; nombre: string; categoria: string; stockBase: number; stockMinimo: number; ubicacion: string | null; imagen: string | null; presentaciones: Array<{ nombre: string; factor: number; stock: number }>; }>;
  ventasPorDia: Array<{ fecha: string; online: number; pos: number; total: number; }>;
  metodosPago: Array<{ metodo: string; cantidad: number; total: number; porcentaje: number; }>;
}

const formatMoney = (value: number) => `$${Math.round(value).toLocaleString('es-CO')}`;
const formatNumber = (value: number) => value.toLocaleString('es-CO');
const formatCompact = (value: number) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
  return `$${value}`;
};

// Fecha en zona horaria Colombia (YYYY-MM-DD)
const fechaColombia = (date = new Date()) => toColombiaDateString(date);

const diaSemana = (fechaStr: string) => {
  const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const d = new Date(fechaStr + 'T00:00:00');
  return dias[d.getDay()];
};

const metodoIcono: Record<string, string> = {
  EFECTIVO: '💵', TARJETA: '💳', CARD: '💳', NEQUI: '📱',
  DAVIPLATA: '📱', TRANSFERENCIA: '🏦', PSE: '🏦', BANCOLOMBIA_TRANSFER: '🏦', EFECTY: '🏪',
};

/* ─── Recharts Custom Tooltip ─── */
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="flex items-center gap-2" style={{ color: p.color }}>
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: {formatMoney(p.value)}
        </p>
      ))}
    </div>
  );
}

/* ─── KPI Card ─── */
function KpiCard({ title, value, subtitle, icon: Icon, color, trend }: {
  title: string; value: string; subtitle: string; icon: any; color: string; trend?: { value: number; label: string };
}) {
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    violet: 'bg-violet-50 text-violet-600 border-violet-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    rose: 'bg-rose-50 text-rose-600 border-rose-200',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  };
  const iconBg: Record<string, string> = {
    emerald: 'bg-emerald-500', blue: 'bg-blue-500', violet: 'bg-violet-500',
    amber: 'bg-amber-500', rose: 'bg-rose-500', cyan: 'bg-cyan-500',
  };
  const up = trend && trend.value >= 0;

  return (
    <div className={`rounded-xl border p-5 transition hover:shadow-md ${colorMap[color] || colorMap.emerald}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium opacity-80 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          <p className="text-xs opacity-70 mt-0.5">{subtitle}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg[color] || iconBg.emerald}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {trend && (
        <div className={`mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${up ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend.value)}% {trend.label}
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton Loader ─── */
function SkeletonCard() {
  return <div className="h-28 bg-gray-100 rounded-xl animate-pulse" />;
}

function SkeletonChart() {
  return <div className="h-64 bg-gray-100 rounded-xl animate-pulse" />;
}

/* ─── Main Page ─── */
const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [vendedorFilter, setVendedorFilter] = useState<string>('');
  const [proveedorFilter, setProveedorFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('month'); // today | week | month | custom | YYYY-MM
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showMigrarModal, setShowMigrarModal] = useState(false);

  const { data: vendedores } = useQuery({
    queryKey: ['vendedores-dropdown'],
    queryFn: async () => {
      const r = await UsersService.getUsers(1, 100);
      return (r.data || []).filter((u: any) => u.estado && u.tipoUsuario !== 'cliente');
    },
    staleTime: Infinity,
  });

  const { data: proveedores } = useQuery({
    queryKey: ['proveedores-dropdown'],
    queryFn: async () => {
      const r = await SuppliersService.getAllForSelect();
      return r.data || [];
    },
    staleTime: Infinity,
  });

  const { data: availableMonths } = useQuery({
    queryKey: ['available-months'],
    queryFn: async () => {
      const r = await ReportsService.getAvailableMonths();
      return r.success ? r.data : [];
    },
    staleTime: Infinity,
  });

  // Calcular rango de fechas según filtro seleccionado
  const { fechaInicio, fechaFin } = useMemo(() => {
    const hoy = fechaColombia();
    const nowColombia = getColombiaDate();

    if (dateFilter === 'today') {
      return { fechaInicio: hoy, fechaFin: hoy };
    }
    if (dateFilter === 'week') {
      const hace6Dias = new Date(nowColombia);
      hace6Dias.setDate(nowColombia.getDate() - 6);
      return { fechaInicio: fechaColombia(hace6Dias), fechaFin: hoy };
    }
    if (dateFilter === 'month') {
      const inicioMes = fechaColombia(new Date(nowColombia.getFullYear(), nowColombia.getMonth(), 1));
      return { fechaInicio: inicioMes, fechaFin: hoy };
    }
    if (dateFilter === 'custom') {
      return { fechaInicio: customStart || hoy, fechaFin: customEnd || hoy };
    }
    if (/^\d{4}-\d{2}$/.test(dateFilter)) {
      const [y, m] = dateFilter.split('-').map(Number);
      const inicio = `${dateFilter}-01`;
      const finDate = new Date(y, m, 0);
      const fin = fechaColombia(finDate);
      return { fechaInicio: inicio, fechaFin: fin };
    }
    return { fechaInicio: hoy, fechaFin: hoy };
  }, [dateFilter, customStart, customEnd]);

  const dateFilterLabel = useMemo(() => {
    if (dateFilter === 'today') return 'Hoy';
    if (dateFilter === 'week') return 'Esta semana';
    if (dateFilter === 'month') return 'Este mes';
    if (dateFilter === 'custom') {
      if (customStart && customEnd) return `${customStart} → ${customEnd}`;
      return 'Rango personalizado';
    }
    if (/^\d{4}-\d{2}$/.test(dateFilter)) {
      const [y, m] = dateFilter.split('-').map(Number);
      return `${monthNames[m - 1]} ${y}`;
    }
    return 'Este mes';
  }, [dateFilter, customStart, customEnd]);

  const { data: dashboardData, isLoading: loadingDash, refetch: refetchDash } = useQuery<DashboardData>({
    queryKey: ['dashboard', vendedorFilter, proveedorFilter, fechaInicio, fechaFin],
    queryFn: async () => {
      const r = await ReportsService.getDashboard(vendedorFilter || undefined, proveedorFilter || undefined, fechaInicio, fechaFin);
      if (r.success && r.data) return r.data;
      throw new Error('Error dashboard');
    },
    refetchInterval: 30000,
    staleTime: 15000,
  });

  const { data: totalProductos = 0, isLoading: loadingProd } = useQuery<number>({
    queryKey: ['total-productos'],
    queryFn: async () => {
      const r = await ProductsService.list(1, 1);
      return r.success && r.data?.meta ? r.data.meta.total || 0 : 0;
    },
    refetchInterval: 60000,
  });

  const { data: dailyComp, isLoading: loadingDaily } = useQuery({
    queryKey: ['daily-comparison', vendedorFilter, proveedorFilter],
    queryFn: async () => {
      const r = await ReportsService.getDailyComparison(vendedorFilter || undefined, proveedorFilter || undefined);
      return r.success ? r.data : null;
    },
    refetchInterval: 30000,
  });

  const { data: salesByDayMonth, isLoading: loadingSalesByDayMonth } = useQuery({
    queryKey: ['sales-by-day-month', vendedorFilter, proveedorFilter, fechaInicio, fechaFin],
    queryFn: async () => {
      const r = await ReportsService.getSalesByDay({ fechaInicio, fechaFin, vendedorId: vendedorFilter || undefined, proveedorId: proveedorFilter || undefined });
      return r.success ? r.data : [];
    },
    refetchInterval: 60000,
  });

  const { data: weekComp, isLoading: loadingWeek } = useQuery({
    queryKey: ['week-comparison', vendedorFilter, proveedorFilter],
    queryFn: async () => {
      const r = await ReportsService.getWeekComparison(vendedorFilter || undefined, proveedorFilter || undefined);
      return r.success ? r.data : null;
    },
    refetchInterval: 30000,
  });

  const { data: salesByHour, isLoading: loadingHour } = useQuery({
    queryKey: ['sales-by-hour', vendedorFilter, proveedorFilter, fechaInicio, fechaFin],
    queryFn: async () => {
      const r = await ReportsService.getSalesByHour({ fechaInicio, fechaFin, vendedorId: vendedorFilter || undefined, proveedorId: proveedorFilter || undefined });
      return r.success ? r.data : [];
    },
    refetchInterval: 30000,
  });

  const { data: salesBySeller, isLoading: loadingSeller } = useQuery({
    queryKey: ['sales-by-seller', vendedorFilter, proveedorFilter, fechaInicio, fechaFin],
    queryFn: async () => {
      const r = await ReportsService.getSalesBySeller({ fechaInicio, fechaFin, vendedorId: vendedorFilter || undefined, proveedorId: proveedorFilter || undefined });
      return r.success ? r.data : [];
    },
    refetchInterval: 30000,
  });

  useOrderWebSocket({ onNewOrder: () => refetchDash(), onStatusChange: () => refetchDash() });

  const data = dashboardData;

  /* ─── Tab: General ─── */
  const GeneralTab = () => {
    if (!data) {
      return (
        <div className="animate-in fade-in duration-300 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="h-6 w-48 bg-gray-100 rounded animate-pulse mb-4" />
              <SkeletonChart />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
              <div className="h-6 w-40 bg-gray-100 rounded animate-pulse mb-4" />
              <SkeletonCard /><SkeletonCard />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="h-6 w-48 bg-gray-100 rounded animate-pulse mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SkeletonCard /><SkeletonCard /><SkeletonCard />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
              <div className="h-6 w-32 bg-gray-100 rounded animate-pulse mb-4" />
              <SkeletonCard /><SkeletonCard />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
              <div className="h-6 w-32 bg-gray-100 rounded animate-pulse mb-4" />
              <SkeletonCard /><SkeletonCard />
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
              <div className="h-6 w-32 bg-gray-100 rounded animate-pulse mb-4" />
              <SkeletonCard /><SkeletonCard />
            </div>
          </div>
        </div>
      );
    }
    const chartData = data.ventasPorDia.map(d => ({
      dia: diaSemana(d.fecha),
      fecha: d.fecha.slice(5),
      Online: d.online,
      POS: d.pos,
      Total: d.total,
    }));

    return (
      <div className="animate-in fade-in duration-300">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {loadingDash || loadingDaily ? (
            <><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
          ) : (
            <>
              <KpiCard title="Ventas Hoy" value={formatMoney(data.hoy.totales.totalGeneral)}
                subtitle={`${formatNumber(data.hoy.totales.totalTransacciones)} transacciones`}
                icon={DollarSign} color="emerald"
                trend={dailyComp ? { value: dailyComp.variacion.total, label: 'vs ayer' } : undefined} />
              <KpiCard title="Ventas del Período" value={formatMoney(data.mes.totales.totalGeneral)}
                subtitle={`${formatNumber(data.mes.totales.totalTransacciones)} transacciones`}
                icon={TrendingUp} color="blue" />
              <KpiCard title="Promedio Diario" value={formatMoney(
                  (() => {
                    const diasConVentas = (salesByDayMonth || []).filter(d => d.total > 0).length;
                    return diasConVentas > 0
                      ? data.mes.totales.totalGeneral / diasConVentas
                      : 0;
                  })()
                )}
                subtitle={`Sobre ${(salesByDayMonth || []).filter(d => d.total > 0).length} días con ventas`} icon={Calendar} color="cyan" />
              <KpiCard title="Ticket Promedio" value={formatMoney(data.hoy.promedios.ticketPromedioGeneral)}
                subtitle="Promedio general hoy" icon={Receipt} color="violet" />
              <KpiCard title="Productos" value={formatNumber(totalProductos)}
                subtitle="Catálogo activo" icon={Package} color="amber" />
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Line Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-600" />
              Evolución de Ventas
            </h3>
            {loadingDash ? <SkeletonChart /> : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={chartData} margin={{ top: 25, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="dia" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCompact} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Online" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="Online" position="top" formatter={(v: any) => formatMoney(v)} fontSize={10} fill="#3b82f6" />
                  </Bar>
                  <Bar dataKey="POS" fill="#10b981" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="POS" position="top" formatter={(v: any) => formatMoney(v)} fontSize={10} fill="#10b981" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Payment Methods */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-emerald-600" />
              Métodos de Pago
            </h3>
            {loadingDash ? <div className="space-y-3"><SkeletonCard /><SkeletonCard /></div> : (
              <div className="space-y-3">
                {data.metodosPago.slice(0, 6).map((mp) => (
                  <div key={mp.metodo}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{metodoIcono[mp.metodo] || '💰'}</span>
                        <span className="text-sm font-medium text-gray-700">{mp.metodo}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatMoney(mp.total)}</p>
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: `${Math.max(2, mp.porcentaje)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Online vs POS + Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-emerald-600" />
              Comparativa Hoy — Online vs POS
            </h3>
            {loadingDash ? <SkeletonChart /> : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700">Online</p>
                    <p className="text-2xl font-bold text-blue-900">{formatMoney(data.hoy.totales.ventasOnline)}</p>
                    <p className="text-xs text-blue-500">{data.hoy.totales.cantidadOnline} pedidos</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <Store className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-emerald-700">POS</p>
                    <p className="text-2xl font-bold text-emerald-900">{formatMoney(data.hoy.totales.ventasPOS)}</p>
                    <p className="text-xs text-emerald-500">{data.hoy.totales.cantidadPOS} pedidos</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-violet-50 border border-violet-100">
                  <div className="w-12 h-12 rounded-xl bg-violet-500 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-violet-700">Total</p>
                    <p className="text-2xl font-bold text-violet-900">{formatMoney(data.hoy.totales.totalGeneral)}</p>
                    <p className="text-xs text-violet-500">{data.hoy.totales.totalTransacciones} trans.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Low Stock Alert */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Stock Bajo
              </h3>
              <button onClick={() => navigate('/dashboard/productos')} className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1">
                Gestionar <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            {loadingDash ? <div className="space-y-3"><SkeletonCard /><SkeletonCard /></div> : (
              <div className="space-y-3">
                {data.lowStockProducts.slice(0, 5).map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg bg-red-50/50 border border-red-100 hover:bg-red-50 transition-colors cursor-pointer" onClick={() => navigate('/dashboard/productos')}>
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                      {p.imagen ? <img src={getProductImageUrl(p.imagen)} alt={p.nombre} className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-gray-400 m-2.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.nombre}</p>
                      <p className="text-[10px] text-gray-400">{p.categoria}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-600">{p.stockBase} ud</p>
                      <p className="text-[10px] text-gray-400">mín {p.stockMinimo}</p>
                    </div>
                  </div>
                ))}
                {data.lowStockProducts.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Package className="w-8 h-8 text-emerald-300 mb-2" />
                    <p className="text-sm text-gray-400">Stock óptimo</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                Más Vendidos
              </h3>
            </div>
            {loadingDash ? <div className="space-y-3"><SkeletonCard /><SkeletonCard /></div> : (
              <div className="space-y-3">
                {data.topProductos.map((p, idx) => (
                  <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-gray-200 text-gray-700' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
                    }`}>{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.nombre}</p>
                      <p className="text-[10px] text-gray-400">{p.categoria}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-emerald-600">{p.cantidadVendida} uds</p>
                      <p className="text-[10px] text-gray-400">{formatMoney(p.totalVentas)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-gray-500" />
                Menos Vendidos
              </h3>
            </div>
            {loadingDash ? <div className="space-y-3"><SkeletonCard /><SkeletonCard /></div> : (
              <div className="space-y-3">
                {data.bottomProductos.map((p, idx) => (
                  <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.nombre}</p>
                      <p className="text-[10px] text-gray-400">{p.categoria}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-600">{p.cantidadVendida} uds</p>
                      <p className="text-[10px] text-gray-400">Stock: {p.stockBase}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ─── Tab: Vendedores ─── */
  const VendedoresTab = () => {
    const totalVentas = useMemo(() => salesBySeller?.reduce((s, v) => s + v.totalVentas, 0) || 1, [salesBySeller]);
    const chartData = useMemo(() =>
      (salesBySeller || []).map(v => ({
        nombre: v.nombre.split(' ')[0],
        Ventas: v.totalVentas,
        Pedidos: v.cantidadPedidos,
        Online: v.online,
        POS: v.pos,
      })),
      [salesBySeller]
    );

    return (
      <div className="animate-in fade-in duration-300 space-y-6">
        {/* Top Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {loadingSeller ? <><SkeletonCard /><SkeletonCard /><SkeletonCard /></> : (
            <>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Award className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">Mejor Vendedor</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{salesBySeller?.[0]?.nombre || '—'}</p>
                <p className="text-sm text-emerald-700 mt-1">{salesBySeller?.[0] ? formatMoney(salesBySeller[0].totalVentas) : '$0'}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Vendedores Activos</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{salesBySeller?.length || 0}</p>
                <p className="text-sm text-blue-700 mt-1">En el período</p>
              </div>
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Receipt className="w-5 h-5 text-violet-600" />
                  <span className="text-sm font-medium text-violet-800">Ticket Promedio</span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {salesBySeller?.length
                    ? formatMoney(salesBySeller.reduce((s, v) => s + v.totalVentas, 0) / salesBySeller.reduce((s, v) => s + v.cantidadPedidos, 0))
                    : '$0'}
                </p>
                <p className="text-sm text-violet-700 mt-1">General equipo</p>
              </div>
            </>
          )}
        </div>

        {/* Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-base font-bold text-gray-900 mb-4">Ventas por Vendedor</h3>
          {loadingSeller ? <SkeletonChart /> : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 50, left: 40, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v: number) => formatMoney(v)} />
                <YAxis type="category" dataKey="nombre" tick={{ fontSize: 12 }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Online" stackId="a" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="POS" stackId="a" fill="#10b981" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="POS" position="right" content={(props: any) => {
                    const { x, y, value, index } = props;
                    const total = chartData[index]?.Ventas || 0;
                    if (!total) return null;
                    return (
                      <text x={(x as number) + 5} y={(y as number) + 12} fill="#ffffff" fontSize={11} fontWeight={600}>
                        {formatMoney(total)}
                      </text>
                    );
                  }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Vendedor</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Ventas</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Pedidos</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Ticket</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">% del Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingSeller ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}><td colSpan={5} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td></tr>
                ))
              ) : (
                salesBySeller?.map((v, i) => (
                  <tr key={v.vendedorId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-200 text-gray-700' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
                        }`}>{i + 1}</span>
                        <span className="font-medium text-gray-900">{v.nombre}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatMoney(v.totalVentas)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{v.cantidadPedidos}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{formatMoney(v.ticketPromedio)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-medium text-emerald-600">{((v.totalVentas / totalVentas) * 100).toFixed(1)}%</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /* ─── Tab: Tendencias ─── */
  const TendenciasTab = () => {
    const weekChart = useMemo(() =>
      (weekComp?.dias || []).map(d => ({
        dia: d.dia,
        Actual: d.semanaActual,
        Anterior: d.semanaAnterior,
      })),
      [weekComp]
    );

    return (
      <div className="animate-in fade-in duration-300 space-y-6">
        {/* Daily Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {loadingDaily ? <><SkeletonCard /><SkeletonCard /></> : (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sun className="w-5 h-5 text-amber-500" />
                  <h3 className="text-base font-bold text-gray-900">Hoy</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-xl font-bold text-gray-900">{formatMoney(dailyComp?.hoy.total || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pedidos</p>
                    <p className="text-xl font-bold text-gray-900">{dailyComp?.hoy.pedidos || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Online</p>
                    <p className="text-sm font-semibold text-blue-600">{formatMoney(dailyComp?.hoy.online || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">POS</p>
                    <p className="text-sm font-semibold text-emerald-600">{formatMoney(dailyComp?.hoy.pos || 0)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Moon className="w-5 h-5 text-indigo-400" />
                  <h3 className="text-base font-bold text-gray-900">Ayer</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-xl font-bold text-gray-900">{formatMoney(dailyComp?.ayer.total || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pedidos</p>
                    <p className="text-xl font-bold text-gray-900">{dailyComp?.ayer.pedidos || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Online</p>
                    <p className="text-sm font-semibold text-blue-600">{formatMoney(dailyComp?.ayer.online || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">POS</p>
                    <p className="text-sm font-semibold text-emerald-600">{formatMoney(dailyComp?.ayer.pos || 0)}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Variation badges */}
        {!loadingDaily && dailyComp && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total', value: dailyComp.variacion.total },
              { label: 'Online', value: dailyComp.variacion.online },
              { label: 'POS', value: dailyComp.variacion.pos },
              { label: 'Pedidos', value: dailyComp.variacion.pedidos },
            ].map((item) => {
              const up = item.value >= 0;
              return (
                <div key={item.label} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border ${up ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                  {up ? <ArrowUpRight className="w-5 h-5 text-emerald-600" /> : <ArrowDownRight className="w-5 h-5 text-rose-600" />}
                  <div className="text-center">
                    <p className={`text-lg font-bold ${up ? 'text-emerald-700' : 'text-rose-700'}`}>{up ? '+' : ''}{item.value}%</p>
                    <p className="text-[10px] text-gray-500">{item.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Week Comparison Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-600" />
              Semana Actual vs Anterior
            </h3>
            {!loadingWeek && weekComp && (
              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                weekComp.totales.variacion >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
              }`}>
                {weekComp.totales.variacion >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {weekComp.totales.variacion}% total
              </div>
            )}
          </div>
          {loadingWeek ? <SkeletonChart /> : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={weekChart} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="dia" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCompact} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Anterior" fill="#d1d5db" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="Anterior" position="top" formatter={(v: any) => formatMoney(v)} fontSize={10} fill="#9ca3af" />
                </Bar>
                <Line type="monotone" dataKey="Actual" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }}>
                  <LabelList dataKey="Actual" position="top" formatter={(v: any) => formatMoney(v)} fontSize={10} fill="#10b981" />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  /* ─── Tab: Horarios ─── */
  const HorariosTab = () => {
    const hourData = useMemo(() => {
      const allHours = Array.from({ length: 24 }, (_, i) => ({
        hora: `${i.toString().padStart(2, '0')}:00`,
        Ventas: 0,
        Transacciones: 0,
        rawHora: i,
      }));
      (salesByHour || []).forEach(h => {
        if (allHours[h.hora]) {
          allHours[h.hora].Ventas = h.ventas;
          allHours[h.hora].Transacciones = h.transacciones;
        }
      });
      return allHours.filter(h => h.rawHora >= 8 && h.rawHora <= 22);
    }, [salesByHour]);

    const pico = useMemo(() => hourData.reduce((max, h) => h.Ventas > max.Ventas ? h : max, hourData[0]), [hourData]);
    const valle = useMemo(() => hourData.reduce((min, h) => h.Ventas < min.Ventas ? h : min, hourData[0]), [hourData]);

    return (
      <div className="animate-in fade-in duration-300 space-y-6">
        {/* Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {loadingHour ? <><SkeletonCard /><SkeletonCard /><SkeletonCard /></> : (
            <>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Hora Pico</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{pico?.hora || '—'}</p>
                <p className="text-sm text-amber-700 mt-1">{formatMoney(pico?.Ventas || 0)} promedio</p>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Moon className="w-5 h-5 text-slate-600" />
                  <span className="text-sm font-medium text-slate-800">Hora Valle</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{valle?.hora || '—'}</p>
                <p className="text-sm text-slate-700 mt-1">{formatMoney(valle?.Ventas || 0)} promedio</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">Transacciones Hoy</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {(salesByHour || []).reduce((s, h) => s + h.transacciones, 0)}
                </p>
                <p className="text-sm text-emerald-700 mt-1">Horario 8:00 a.m. – 10:00 p.m.</p>
              </div>
            </>
          )}
        </div>

        {/* Hour Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-emerald-600" />
            Ventas por Hora del Día
          </h3>
          {loadingHour ? <SkeletonChart /> : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={hourData} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval={1} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCompact} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Ventas" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="Ventas" position="top" formatter={(v: any) => v > 0 ? formatMoney(v) : ''} fontSize={9} />
                  {hourData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.rawHora === pico?.rawHora ? '#f59e0b' : entry.rawHora === valle?.rawHora ? '#94a3b8' : '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Hour Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-base font-bold text-gray-900 mb-4">Mapa de Calor por Hora</h3>
          {loadingHour ? (
            <div className="grid grid-cols-6 gap-2">
              {Array.from({ length: 15 }).map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
              {hourData.map((h) => {
                const maxVal = pico?.Ventas || 1;
                const intensity = Math.min(1, h.Ventas / maxVal);
                const bg = intensity === 0 ? 'bg-gray-50' : intensity < 0.3 ? 'bg-emerald-100' : intensity < 0.6 ? 'bg-emerald-300' : intensity < 0.8 ? 'bg-emerald-500' : 'bg-emerald-700';
                const text = intensity > 0.5 ? 'text-white' : 'text-emerald-800';
                return (
                  <div key={h.hora} className={`${bg} ${text} rounded-lg p-2 text-center transition hover:scale-105`}>
                    <p className="text-[10px] font-medium opacity-80">{h.hora}</p>
                    <p className="text-xs font-bold">{formatCompact(h.Ventas)}</p>
                    <p className="text-[9px] opacity-70">{h.Transacciones} ped</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-emerald-600" />
            Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Centro de comando — {new Date().toLocaleDateString('es-CO', { timeZone: 'America/Bogota', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            {dateFilter !== 'today' && (
              <span className="ml-2 text-emerald-600 font-medium">· {dateFilterLabel}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Date filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <Listbox value={dateFilter} onChange={setDateFilter}>
              <div className="relative min-w-[200px]">
                <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white border border-gray-200 py-2 pl-3 pr-10 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                  <span className="block truncate">{dateFilterLabel}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </span>
                </Listbox.Button>
                <Transition
                  as="div"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg bg-white border border-gray-200 py-1 text-sm shadow-lg focus:outline-none">
                    <Listbox.Option
                      value="today"
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>Hoy</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                    <Listbox.Option
                      value="week"
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>Esta semana</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                    <Listbox.Option
                      value="month"
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>Este mes</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                    {availableMonths && availableMonths.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Meses con ventas</div>
                        {availableMonths.map((ym) => (
                          <Listbox.Option
                            key={ym}
                            value={ym}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                            }
                          >
                            {({ selected }) => {
                              const [y, m] = ym.split('-').map(Number);
                              return (
                                <>
                                  <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{monthNames[m - 1]} {y}</span>
                                  {selected && (
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                                      <Check className="w-4 h-4" />
                                    </span>
                                  )}
                                </>
                              );
                            }}
                          </Listbox.Option>
                        ))}
                      </>
                    )}
                    <div className="border-t border-gray-100 my-1" />
                    <Listbox.Option
                      value="custom"
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>Rango personalizado</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
            {dateFilter !== 'month' && (
              <button
                onClick={() => setDateFilter('month')}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                title="Restablecer filtro"
              >
                <Minus className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Custom date range inputs */}
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="rounded-lg border border-gray-200 py-2 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
              <span className="text-gray-400 text-sm">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="rounded-lg border border-gray-200 py-2 px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
          )}

          {/* Vendedor filter */}
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <Listbox value={vendedorFilter} onChange={setVendedorFilter}>
              <div className="relative min-w-[200px]">
                <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white border border-gray-200 py-2 pl-3 pr-10 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                  <span className="block truncate">
                    {vendedorFilter ? vendedores?.find((v: any) => v.id === vendedorFilter)?.nombreCompleto : 'Todos los vendedores'}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </span>
                </Listbox.Button>
                <Transition
                  as="div"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white border border-gray-200 py-1 text-sm shadow-lg focus:outline-none">
                    <Listbox.Option
                      value=""
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>Todos los vendedores</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                    {vendedores?.map((v: any) => (
                      <Listbox.Option
                        key={v.id}
                        value={v.id}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{v.nombreCompleto}</span>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                                <Check className="w-4 h-4" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
            {vendedorFilter && (
              <button
                onClick={() => setVendedorFilter('')}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                title="Limpiar filtro"
              >
                <Minus className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Proveedor filter */}
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-gray-400" />
            <Listbox value={proveedorFilter} onChange={setProveedorFilter}>
              <div className="relative min-w-[200px]">
                <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white border border-gray-200 py-2 pl-3 pr-10 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                  <span className="block truncate">
                    {proveedorFilter ? proveedores?.find((p: any) => p.id === proveedorFilter)?.nombre : 'Todos los proveedores'}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </span>
                </Listbox.Button>
                <Transition
                  as="div"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white border border-gray-200 py-1 text-sm shadow-lg focus:outline-none">
                    <Listbox.Option
                      value=""
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>Todos los proveedores</span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                              <Check className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                    {proveedores?.map((p: any) => (
                      <Listbox.Option
                        key={p.id}
                        value={p.id}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700'}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{p.nombre}</span>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600">
                                <Check className="w-4 h-4" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
            {proveedorFilter && (
              <button
                onClick={() => setProveedorFilter('')}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                title="Limpiar filtro"
              >
                <Minus className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Migrar lotes (acción temporal) */}
          <div className="h-6 w-px bg-gray-200 mx-1 hidden sm:block" />
          <button
            onClick={() => setShowMigrarModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
            title="Migrar lotes históricos"
          >
            <Database className="w-4 h-4" />
            <span className="hidden sm:inline">Migrar lotes</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'general', label: 'General' },
          { id: 'vendedores', label: 'Vendedores' },
          { id: 'tendencias', label: 'Tendencias' },
          { id: 'horarios', label: 'Horarios' },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {/* Content — mantener montado para evitar re-render de gráficos */}
      <div className={activeTab === 'general' ? '' : 'hidden'}><GeneralTab /></div>
      <div className={activeTab === 'vendedores' ? '' : 'hidden'}><VendedoresTab /></div>
      <div className={activeTab === 'tendencias' ? '' : 'hidden'}><TendenciasTab /></div>
      <div className={activeTab === 'horarios' ? '' : 'hidden'}><HorariosTab /></div>

      <MigrarLotesModal isOpen={showMigrarModal} onClose={() => setShowMigrarModal(false)} />
    </div>
  );
};

export default DashboardPage;
